import { AlertTriangle, Info, X } from "lucide-react";
import React from "react";
import { createPortal } from "react-dom";

export interface ConfirmationDialogProps {
  isOpen: boolean;
  title?: string;
  message?: string | React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
  type?: "danger" | "warning" | "info";
  isLoading?: boolean;
  error?: string;
  children?: React.ReactNode;
}

const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  isOpen,
  title = "Confirm Action",
  message,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  onConfirm,
  onCancel,
  type = "danger",
  isLoading = false,
  error,
  children,
}) => {
  if (!isOpen) return null;

  const getTypeStyles = () => {
    switch (type) {
      case "danger":
        return {
          icon: <AlertTriangle className="text-red-500" size={24} />,
          confirmButton: "bg-red-600 hover:bg-red-700 focus:ring-red-500",
        };
      case "warning":
        return {
          icon: <AlertTriangle className="text-yellow-500" size={24} />,
          confirmButton:
            "bg-yellow-600 hover:bg-yellow-700 focus:ring-yellow-500",
        };
      case "info":
        return {
          icon: <Info className="text-blue-500" size={24} />,
          confirmButton: "bg-blue-600 hover:bg-blue-700 focus:ring-blue-500",
        };
      default:
        return {
          icon: <AlertTriangle className="text-red-500" size={24} />,
          confirmButton: "bg-red-600 hover:bg-red-700 focus:ring-red-500",
        };
    }
  };

  const typeStyles = getTypeStyles();

  const dialogContent = (
    <div
      className="fixed inset-0 overflow-y-auto"
      style={{
        zIndex: 999999,
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
      }}
    >
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/10"
        style={{
          zIndex: 999998,
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
        }}
        onClick={onCancel}
      />

      {/* Dialog */}
      <div
        className="flex min-h-full items-center justify-center p-4"
        style={{
          zIndex: 999999,
          position: "relative",
        }}
      >
        <div
          className="relative bg-card text-card-foreground rounded shadow-2xl max-w-md w-full border border-border"
          style={{
            zIndex: 999999,
            position: "relative",
          }}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-border">
            <div className="flex items-center gap-3">
              {typeStyles.icon}
              <h3 className="text-lg font-semibold text-foreground">{title}</h3>
            </div>
            <button
              onClick={onCancel}
              className="text-muted-foreground hover:text-foreground transition-colors"
              disabled={isLoading}
            >
              <X size={20} />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            {message && (
              <div className="text-foreground text-sm leading-relaxed mb-2">
                {message}
              </div>
            )}
            {children}
            {error && (
              <div className="mt-2 text-sm text-red-600 dark:text-red-400 font-semibold">
                {error}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 p-6 border-t border-border">
            <button
              onClick={onCancel}
              disabled={isLoading}
              className="px-4 py-2 text-sm font-medium text-muted-foreground bg-secondary hover:bg-secondary/80 rounded transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {cancelLabel}
            </button>
            <button
              onClick={onConfirm}
              disabled={isLoading}
              className={`px-4 py-2 text-sm font-medium text-white rounded transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${typeStyles.confirmButton}`}
            >
              {isLoading ? "Processing..." : confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // Use portal to render outside of the component tree
  return typeof document !== "undefined"
    ? createPortal(dialogContent, document.body)
    : null;
};

export default ConfirmationDialog;
